import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import expenseService from '@api/v1/expenseService'
import matterService from '@api/v1/matterService'

const ExpenseForm = ({ onSave, onCancel, saving }) => {
  const [form, setForm] = useState({
    matterId: '',
    matterSearch: '',
    date: dayjs().format('YYYY-MM-DD'),
    amount: '',
    description: '',
    billingCodeId: '',
    isBillable: true,
  })

  const { data: mattersData } = useQuery({
    queryKey: ['matters', 1],
    queryFn: () => matterService.getMattersEnriched({
      activeStatus: 1, limit: 500, offset: 0,
    }),
  })

  const { data: codes = [] } = useQuery({
    queryKey: ['expenseCodes'],
    queryFn: () => expenseService.getExpenseCodes(),
  })

  const matters = mattersData?.matters || []

  const filteredMatters = form.matterSearch.length >= 2
    ? matters.filter((m) => {
      const s = form.matterSearch.toLowerCase()
      return (m.name || '').toLowerCase().includes(s)
          || (m.number || '').toLowerCase().includes(s)
          || (m.clientName || '').toLowerCase().includes(s)
    })
    : []

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const selectMatter = (matter) => {
    setForm((prev) => ({
      ...prev,
      matterId: matter.id,
      matterSearch: matter.name || matter.fullName || '',
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!form.matterId || !form.description.trim() || !form.amount) return
    onSave({
      matterId: form.matterId,
      date: form.date,
      amount: parseFloat(form.amount) || 0,
      description: form.description,
      billingCodeId: form.billingCodeId || undefined,
      isBillable: form.isBillable,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={form.date}
            onChange={(e) => handleChange('date', e.target.value)}
            required
            className="w-full px-3 py-2 bg-white border border-gray-200
              rounded-lg text-sm text-gray-900
              focus:outline-none focus:ring-2 focus:ring-cb-accent/30
              focus:border-cb-accent"
          />
        </div>

        {/* Amount */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Amount ($) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            step="0.01"
            value={form.amount}
            onChange={(e) => handleChange('amount', e.target.value)}
            required
            placeholder="25.00"
            className="w-full px-3 py-2 bg-white border border-gray-200
              rounded-lg text-sm text-gray-900 placeholder:text-gray-500
              focus:outline-none focus:ring-2 focus:ring-cb-accent/30
              focus:border-cb-accent"
          />
        </div>

        {/* Matter Search */}
        <div className="sm:col-span-2 relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Matter <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.matterSearch}
            onChange={(e) => {
              handleChange('matterSearch', e.target.value)
              if (form.matterId) handleChange('matterId', '')
            }}
            placeholder="Search for a matter..."
            className={`w-full px-3 py-2 bg-white border rounded-lg text-sm
              text-gray-900 placeholder:text-gray-500
              focus:outline-none focus:ring-2 focus:ring-cb-accent/30
              focus:border-cb-accent ${
                form.matterId ? 'border-cb-teal' : 'border-gray-200'
              }`}
          />
          {form.matterId && (
            <span className="absolute right-3 top-8 text-xs text-cb-teal">
              Selected
            </span>
          )}
          {filteredMatters.length > 0 && !form.matterId && (
            <div className="absolute z-10 mt-1 w-full bg-white border
              border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
            >
              {filteredMatters.slice(0, 10).map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => selectMatter(m)}
                  className="w-full text-left px-3 py-2 text-sm
                    hover:bg-gray-50 border-b border-gray-50 last:border-0"
                >
                  <span className="font-medium text-gray-900">
                    {m.name || m.fullName}
                  </span>
                  {m.number && (
                    <span className="text-gray-500 ml-2">#{m.number}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Billing Code */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Expense Code
          </label>
          <select
            value={form.billingCodeId}
            onChange={(e) => handleChange('billingCodeId', e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-200
              rounded-lg text-sm text-gray-900
              focus:outline-none focus:ring-2 focus:ring-cb-accent/30
              focus:border-cb-accent"
          >
            <option value="">-- Select Code --</option>
            {codes.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        {/* Billable toggle */}
        <div className="flex items-end pb-1">
          <div className="flex items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={form.isBillable}
              aria-label="Billable"
              onClick={() => handleChange('isBillable', !form.isBillable)}
              className={`relative w-11 h-6 rounded-full transition-colors ${
                form.isBillable ? 'bg-cb-accent' : 'bg-gray-200'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white
                  rounded-full shadow transition-transform ${
                    form.isBillable ? 'translate-x-5' : ''
                  }`}
              />
            </button>
            <span className="text-sm text-gray-700">Billable</span>
          </div>
        </div>

        {/* Description */}
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            rows={3}
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            required
            placeholder="Describe the expense..."
            className="w-full px-3 py-2 bg-white border border-gray-200
              rounded-lg text-sm text-gray-900 placeholder:text-gray-500
              focus:outline-none focus:ring-2 focus:ring-cb-accent/30
              focus:border-cb-accent resize-none"
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white
            border border-gray-200 rounded-lg hover:bg-gray-50
            transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={
            saving || !form.matterId || !form.description.trim() || !form.amount
          }
          className="px-4 py-2 text-sm font-medium text-white bg-cb-accent
            rounded-lg hover:bg-cb-accent/90 transition-colors shadow-sm
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Create Expense'}
        </button>
      </div>
    </form>
  )
}

export default ExpenseForm
